import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { database, firestore } from "../firebaseConfig";
import { ref, onValue } from "firebase/database";
import { 
  doc, 
  updateDoc, 
  deleteDoc,
  collection,
  query,
  orderBy,
  startAfter,
  limit,
  getDocs,
  where,
  getCountFromServer
} from 'firebase/firestore';
import "./WorldDbTable.css";
import WorldDbTableNav from './WorldDbTableNav';
import EmailAiPopup from './EmailAiPopup';
import EmailPopup from './EmailPopup';
import BulkEmailPopup from './BulkEmailPopup';
import MassSpyPopup from './MassSpyPopup';
import AiCleanerPopup from './AiCleanerPopup';
import useDebounce from "../hooks/use-debounce";

// Mapping der Spaltennamen für lesbare Überschriften
const availableFields = {
  lead_firstname: "First Name",
  lead_lastname: "Last Name",
  lead_email: "Email",
  lead_telephone: "Telephone",
  lead_address: "Address",
  lead_city: "City",
  lead_postcode: "Postcode",
  lead_country: "Country",
  business_name: "Business Name",
  lead_website: "Website",
  lead_instagram: "Instagram",
  lead_linkedin: "LinkedIn",
  lead_tiktok: "TikTok",
  lead_rating: "Rating",
  lead_reviews: "Reviews",
  lead_link: "Link",
  lead_image: "Image",
  lead_description: "Description",
  lead_stars: "Stars",
  lead_type: "Type",
  lead_facebook: "Facebook",
  lead_reply: "Has Replied",
  timestamp: "Added",
  import_name: "Import"
};

// Dropdown-Optionen für Type
const typeOptions = ["Small Hotel", "Big Hotel", "Airbnb"];

const columnOrder = [
  "selectCheckbox",
  "rowNumber",
  "contacted",
  "aiMail",
  "whatsapp",
  "spy",
  "business_name",
  "lead_description",
  "lead_email",
  "lead_telephone",
  "lead_website",
  "lead_rating",
  "lead_reviews",
  "lead_instagram",
  "lead_facebook",
  "lead_tiktok",
  "lead_linkedin",
  ...Object.keys(availableFields).filter(
    (key) =>
      ![
        "lead_type",
        "lead_description",
        "business_name",
        "lead_email",
        "lead_telephone",
        "lead_website",
        "lead_instagram",
        "lead_facebook",
        "lead_tiktok",
        "lead_linkedin",
        "lead_reply",
        "lead_rating",
        "lead_reviews"
      ].includes(key)
  ),
  "lead_type",
  "lead_reply",
  "lastEdited"
];

const PAGE_SIZE = 100;

const NewWorldDbTable = ({ 
  userId, 
  setShowUploader, 
  totalDatabaseCount,
  contactedToday,
  onEmailResult,
  onReload: parentOnReload,
  reloadFlag
}) => {
  const [dbData, setDbData] = useState([]);
  const [editedData, setEditedData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);
  const itemsToLoadRef = useRef(PAGE_SIZE);
  
  const [sortField, setSortField] = useState("timestamp");
  const [sortDirection, setSortDirection] = useState("desc");
  const [statusFilter, setStatusFilter] = useState("Not Contacted");
  const [importFilter, setImportFilter] = useState("all");
  const [importOptions, setImportOptions] = useState([]);
  const [filteredCount, setFilteredCount] = useState(null);
  const [noFilterResults, setNoFilterResults] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [advancedFilters, setAdvancedFilters] = useState({
    city: '',
    has_email: 'any',
    has_website: 'any',
    has_instagram: 'any',
    range_field: 'lead_rating',
    range_min: '',
    range_max: '',
  });
  const [searchTerm, setSearchTerm] = useState("");
  // Reduziert Firestore-Reads bei schneller Eingabe
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [emailPopupData, setEmailPopupData] = useState({
    show: false,
    rowData: null,
    index: null
  });

  // Stelle ScrollPosition wieder her, wenn Popup geschlossen wurde
  useEffect(() => {
    if (!emailPopupData.show) {
      const id = setTimeout(() => {
        restoreScrollPosition();
      }, 50); // kleiner Delay, bis DOM umgebaut ist
      return () => clearTimeout(id);
    }
  }, [emailPopupData.show]);

  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [lastSelectedRowId, setLastSelectedRowId] = useState(null);
  
  const tableContainerRef = useRef(null);
  const scrollPositionRef = useRef({ top: 0, left: 0 });
  const [pendingEdits, setPendingEdits] = useState({});
  const [activeEditField, setActiveEditField] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const currentEditValue = useRef('');

  const [isAiManagerOpen, setIsAiManagerOpen] = useState(false);
  const [isAutoSendOpen, setIsAutoSendOpen] = useState(false);
  const [bulkSelectedRows, setBulkSelectedRows] = useState([]);
  const [bulkContactsData, setBulkContactsData] = useState([]);

  const [isAiCleanerOpen, setIsAiCleanerOpen] = useState(false);
  const [cleanerResults, setCleanerResults] = useState(null);

  const [spyLoadingRows, setSpyLoadingRows] = useState({});

  const [isMassSpyActive, setIsMassSpyActive] = useState(false);
  const [massSpyQueue, setMassSpyQueue] = useState([]);
  const [massSpyProgress, setMassSpyProgress] = useState(0);
  const [massSpyTotal, setMassSpyTotal] = useState(0);
  const [massSpyResults, setMassSpyResults] = useState(null);
  const [massSpyContacts, setMassSpyContacts] = useState([]);

  const tableRef = useRef(null);
  // Scroll-Lock: blockiert Überschreiben der gespeicherten Scroll-Position, solange das Popup offen
  const scrollLockedRef = useRef(false);

  const handleEmailResult = useCallback(
    (status, message, ...rest) => {
      if (!message) {
        if (onEmailResult) onEmailResult(status, message, ...rest);
        return;
      }
      // Speichere die Scroll-Position vor jeder Aktualisierung
      saveScrollPosition();
      // Kontakt wurde gelöscht -> entferne sofort aus der Tabelle
      if (status === 'contact-deleted' && typeof message === 'string') {
        setDbData(prev => prev.filter(item => item && item.id !== message));
        restoreScrollPosition();
        return;
      }
      // Status-Updates direkt in die Tabelle patchen
      if (status === 'status-update' && message.id) {
        // Finde den betroffenen Datensatz in der lokalen Tabelle
        const rowIndex = dbData.findIndex(item => item && item.id === message.id);
        if (rowIndex !== -1) {
          setDbData(prevData => {
            const newData = [...prevData];
            newData[rowIndex] = {
              ...newData[rowIndex],
              [message.field]: message.value,
              ...(message.field === 'lead_contacted' ? { lead_status: message.value } : {}),
              ...(message.field === 'lead_status' ? { lead_contacted: message.value } : {}),
              lead_last_email: {
                ...newData[rowIndex].lead_last_email,
                timestamp: message.timestamp,
                status: message.value,
              },
              updated_at: message.timestamp,
              last_contact_date: new Date(message.timestamp).toISOString(),
            };
            return newData;
          });
          setEditedData(prev => ({ ...prev, [`${message.id}-${message.field}`]: message.value }));
        }
      }
      if (onEmailResult) onEmailResult(status, message, ...rest);
      setTimeout(restoreScrollPosition, 100);
    },
    [dbData, onEmailResult]
  );

  useEffect(() => {
    itemsToLoadRef.current = PAGE_SIZE;
    fetchData(true);
  }, [userId, sortField, sortDirection, statusFilter, importFilter, advancedFilters, debouncedSearchTerm]);

  useEffect(() => {
    fetchData(true);
  }, [reloadFlag]);

  const fetchData = async (isInitial = true) => {
    if (!userId) return;

    if (isInitial) {
      setLoading(true);
      setLastDoc(null);
      setHasMoreData(true);
      setNoFilterResults(false);
    } else {
      setLoadingMore(true);
    }

    try {
      const dbRef = collection(firestore, `World/${userId}/db`);
      let currentQuery;

      let baseQuery = query(dbRef);

      // Search Filter  ─ jetzt mit Beschreibung (lead_description)
      const effectiveSearchTerm = debouncedSearchTerm;

      // Spezial-Handling, falls eine Suche aktiv ist.
      if (effectiveSearchTerm) {
        const normalizedSearchTerm = effectiveSearchTerm.toLowerCase();

        if (normalizedSearchTerm.includes('@')) {
          // E-Mail-Suche wie gehabt (ein Query)
          baseQuery = query(
            baseQuery,
            where('lead_email', '>=', normalizedSearchTerm),
            where('lead_email', '<=', normalizedSearchTerm + '\uf8ff')
          );
          // Sortierung weiter unten ⇢ lead_email
        } else {
          // Wir wollen Name *oder* Description matchen. Firestore unterstützt hier kein einfaches OR mit Range-Queries + Pagination.
          // Deshalb: Zwei einzelne Queries ausführen und die Resultate client-seitig mergen (max PAGE_SIZE).

          const baseForText = baseQuery; // beinhaltet Status/Import/Advanced-Filter, aber noch kein Search-Filter

          const qName = query(
            baseForText,
            where('business_name_normalized', '>=', normalizedSearchTerm),
            where('business_name_normalized', '<=', normalizedSearchTerm + '\uf8ff'),
            orderBy('business_name_normalized'),
            limit(itemsToLoadRef.current)
          );

          const qDesc = query(
            baseForText,
            where('lead_description_normalized', '>=', normalizedSearchTerm),
            where('lead_description_normalized', '<=', normalizedSearchTerm + '\uf8ff'),
            orderBy('lead_description_normalized'),
            limit(itemsToLoadRef.current)
          );

          const [snapName, snapDesc] = await Promise.all([getDocs(qName), getDocs(qDesc)]);

          const mergedMap = new Map();
          snapName.docs.forEach(doc => mergedMap.set(doc.id, doc));
          snapDesc.docs.forEach(doc => mergedMap.set(doc.id, doc));

          const mergedDocs = Array.from(mergedMap.values()).slice(0, itemsToLoadRef.current);

          const newData = mergedDocs.map(doc => ({ id: doc.id, ...doc.data() }));

          if (isInitial && newData.length === 0) {
            setNoFilterResults(true);
          }

          setDbData(newData);
          setLastDoc(null); // Für aktive Suche kein Pagination-Weiterblättern
          setHasMoreData(false);

          setLoading(false);
          setLoadingMore(false);
          return; // komplette fetchData-Routine beendet
        }
      }

      // Filter functionality
      if (statusFilter !== 'all') {
        if (statusFilter === 'Not Contacted') {
            baseQuery = query(baseQuery, where('lead_contacted', '==', 'Not Contacted'));
        } else if (statusFilter === 'ready_to_spy') {
            baseQuery = query(baseQuery, where('is_ready_for_spy', '==', true));
        } else {
            baseQuery = query(baseQuery, where('lead_contacted', '==', statusFilter));
        }
      }
      if (importFilter !== 'all') {
        baseQuery = query(baseQuery, where('import_name', '==', importFilter));
      }

      // Advanced Filters
      if (advancedFilters.city) {
        baseQuery = query(baseQuery, where('lead_city', '==', advancedFilters.city));
      }
      if (advancedFilters.has_email !== 'any') {
        baseQuery = query(baseQuery, where('has_email', '==', advancedFilters.has_email === 'true'));
      }
      if (advancedFilters.has_website !== 'any') {
        baseQuery = query(baseQuery, where('has_website', '==', advancedFilters.has_website === 'true'));
      }
      if (advancedFilters.has_instagram !== 'any') {
        baseQuery = query(baseQuery, where('has_instagram', '==', advancedFilters.has_instagram === 'true'));
      }
      // Range filter - can only apply one at a time.
      if (advancedFilters.range_field) {
        if (advancedFilters.range_min) {
          baseQuery = query(baseQuery, where(advancedFilters.range_field, '>=', Number(advancedFilters.range_min)));
        }
        if (advancedFilters.range_max) {
          baseQuery = query(baseQuery, where(advancedFilters.range_field, '<=', Number(advancedFilters.range_max)));
        }
      }

      // Sorting must be the last query composer
      let effectiveSortField = sortField;
      let effectiveSortDirection = sortDirection;
      
      if (effectiveSearchTerm) {
        if (effectiveSearchTerm.toLowerCase().includes('@')) {
          effectiveSortField = 'lead_email';
        } else {
          effectiveSortField = 'business_name_normalized';
        }
        effectiveSortDirection = 'asc';
      } else {
        effectiveSortField = sortField === 'business_name' ? 'business_name_normalized' : sortField;
      }
      currentQuery = query(baseQuery, orderBy(effectiveSortField, effectiveSortDirection));

      if (isInitial) {
        currentQuery = query(currentQuery, limit(itemsToLoadRef.current));
      } else {
        if (!lastDoc) {
          setLoadingMore(false);
          return;
        }
        currentQuery = query(currentQuery, startAfter(lastDoc), limit(PAGE_SIZE));
      }

      const documentSnapshots = await getDocs(currentQuery);
      
      const newData = documentSnapshots.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));

      if (isInitial && newData.length === 0) {
        setNoFilterResults(true);
      }

      setDbData(prevData => isInitial ? newData : [...prevData, ...newData]);

      const newLastDoc = documentSnapshots.docs[documentSnapshots.docs.length - 1];
      setLastDoc(newLastDoc);

      if (documentSnapshots.docs.length < PAGE_SIZE) {
        setHasMoreData(false);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const onReload = () => {
    itemsToLoadRef.current = dbData.length > 0 ? dbData.length : PAGE_SIZE;
    fetchData(true);
    if(parentOnReload) parentOnReload();
  }

  useEffect(() => {
    const getFilteredCount = async () => {
      if (!userId) return;

      const isFilterActive = statusFilter !== 'all' || importFilter !== 'all' || Object.values(advancedFilters).some(value => value !== 'any' && value !== '');
      if (!isFilterActive) {
        setFilteredCount(null);
        return;
      }

      const dbRef = collection(firestore, `World/${userId}/db`);
      let countQuery = query(dbRef);

      if (statusFilter !== 'all') {
        if (statusFilter === 'ready_to_spy') {
          countQuery = query(countQuery, where('is_ready_for_spy', '==', true));
        } else {
          countQuery = query(countQuery, where('lead_contacted', '==', statusFilter));
        }
      }
      if (importFilter !== 'all') {
        countQuery = query(countQuery, where('import_name', '==', importFilter));
      }

      // Add advanced filters to count query
      if (advancedFilters.city) {
        countQuery = query(countQuery, where('lead_city', '==', advancedFilters.city));
      }
      if (advancedFilters.has_email !== 'any') {
        countQuery = query(countQuery, where('has_email', '==', advancedFilters.has_email === 'true'));
      }
      if (advancedFilters.has_website !== 'any') {
        countQuery = query(countQuery, where('has_website', '==', advancedFilters.has_website === 'true'));
      }
      if (advancedFilters.has_instagram !== 'any') {
        countQuery = query(countQuery, where('has_instagram', '==', advancedFilters.has_instagram === 'true'));
      }
      if (advancedFilters.range_min) {
        countQuery = query(countQuery, where(advancedFilters.range_field, '>=', Number(advancedFilters.range_min)));
      }
      if (advancedFilters.range_max) {
        countQuery = query(countQuery, where(advancedFilters.range_field, '<=', Number(advancedFilters.range_max)));
      }

      try {
        const snapshot = await getCountFromServer(countQuery);
        setFilteredCount(snapshot.data().count);
      } catch (error) {
        console.error("Error getting filtered count:", error);
        setFilteredCount(0);
      }
    };

    getFilteredCount();
  }, [userId, statusFilter, importFilter, advancedFilters, reloadFlag]);

  useEffect(() => {
    const fetchAllImportOptions = async () => {
      if (!userId) return;
      try {
        // This now reads from the dedicated, efficient metadata collection
        const importNamesRef = collection(firestore, `World/${userId}/import_names`);
        const q = query(importNamesRef, orderBy('name', 'asc'));
        const documentSnapshots = await getDocs(q);
        
        const uniqueImports = documentSnapshots.docs.map(doc => doc.data().name);
        
        setImportOptions(uniqueImports);
      } catch (error) {
          console.error("Error fetching all import options:", error);
      }
    };
    
    fetchAllImportOptions();
  }, [userId]);

  const saveScrollPosition = () => {
    // Während das Popup geöffnet ist ODER solange wir noch nicht wiederhergestellt haben,
    // darf die Scroll-Position nicht überschrieben werden.
    if (emailPopupData.show || scrollLockedRef.current) return;
    if (tableContainerRef.current) {
      scrollPositionRef.current = {
        top: tableContainerRef.current.scrollTop,
        left: tableContainerRef.current.scrollLeft,
      };
    }
  };

  const restoreScrollPosition = () => {
    if (tableContainerRef.current) {
      tableContainerRef.current.scrollTop = scrollPositionRef.current.top;
      tableContainerRef.current.scrollLeft = scrollPositionRef.current.left;
      // Scroll-Position wurde wiederhergestellt → Lock aufheben
      scrollLockedRef.current = false;
    }
  };

  const handleWheel = (e) => {
    if (!isEditing) {
      saveScrollPosition();
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: true });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [isEditing]);

  const selectedCount = Object.values(selectedRows).filter(Boolean).length;
  const displayedCount = useMemo(() => dbData.length, [dbData]);

  const handleSortChange = (field, direction) => {
    saveAllPendingEdits();
    saveScrollPosition();
    setSortField(field);
    setSortDirection(direction);
  };
  
  const renderSortButtons = (colKey) => {
    // Determine if sorting by this column is currently possible
    const isSortable = sortableColumns.includes(colKey);
    let isDisabled = false;
    let tooltip = '';

    if (searchTerm) {
      isDisabled = true;
      tooltip = 'Sorting is disabled during search.';
    }

    if (!isSortable) return null;

    const isActive = !searchTerm && sortField === colKey;
    return (
      <div className="sort-controls" title={tooltip}>
        <button 
          className={`sort-up-btn ${isActive && sortDirection === 'asc' ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleSortChange(colKey, 'asc'); }}
          disabled={isDisabled}
          title={isDisabled ? tooltip : "Aufsteigend sortieren"}
        >
          ▲
        </button>
        <button 
          className={`sort-down-btn ${isActive && sortDirection === 'desc' ? 'active' : ''}`}
          onClick={(e) => { e.stopPropagation(); handleSortChange(colKey, 'desc'); }}
          disabled={isDisabled}
          title={isDisabled ? tooltip : "Absteigend sortieren"}
        >
          ▼
        </button>
      </div>
    );
  };

  const orderedColumns = columnOrder;

  const saveAllPendingEdits = () => {
    Object.entries(pendingEdits).forEach(([key, value]) => {
      const [rowId, fieldKey] = key.split('-');
      if (rowId && fieldKey) {
        saveToDatabase(rowId, fieldKey, value);
      }
    });
    setPendingEdits({});
  };
  
  const displayRows = useMemo(() => {
    return dbData.filter(row => row !== undefined && row !== null).map((row, index) => {
      if (!row.id) return { ...row, id: String(index) };
      return row;
    });
  }, [dbData]);

  const handleEdit = (rowId, key, value) => {
    saveScrollPosition();
    setIsEditing(true);
    
    setEditedData((prev) => ({
      ...prev,
      [`${rowId}-${key}`]: value,
    }));

    setPendingEdits(prev => ({
      ...prev,
      [`${rowId}-${key}`]: value
    }));

    setDbData(prevData => {
      return prevData.map(row => {
        if (row && row.id === rowId) {
          return {
            ...row,
            [key]: value,
            lead_last_edit: {
              time: new Date().toISOString(),
              user: userId
            },
          };
        }
        return row;
      });
    });

    saveToDatabase(rowId, key, value);
    setTimeout(restoreScrollPosition, 50);
  };

  const saveToDatabase = (rowId, key, value) => {
    if (!userId || !rowId) {
      setIsEditing(false);
      return;
    }
    
    setPendingEdits(prev => {
      const newPending = { ...prev };
      delete newPending[`${rowId}-${key}`];
      return newPending;
    });
    
    const rowRef = doc(firestore, `World/${userId}/db/${rowId}`);
    
    const updates = { 
      [key]: value,
      lead_last_edit: {
        time: new Date().toISOString(),
        user: userId
      }
    };

    if (key === 'business_name') {
      updates.business_name_normalized = typeof value === 'string' ? value.toLowerCase() : '';
    }
    if (key === 'lead_description') {
      updates.lead_description_normalized = typeof value === 'string' ? value.toLowerCase() : '';
    }
    
    updateDoc(rowRef, updates)
      .then(() => {
        setTimeout(() => {
          setIsEditing(false);
          restoreScrollPosition();
        }, 300);
      })
      .catch((error) => {
        console.error("Fehler beim Speichern:", error);
        setIsEditing(false);
        restoreScrollPosition();
      });
  };
  
  const loadMoreData = () => {
    if (hasMoreData && !loadingMore) {
      itemsToLoadRef.current = dbData.length + PAGE_SIZE;
      fetchData(false);
    }
  };

  // ... (Hier folgen die restlichen Handler-Funktionen, die weitgehend gleich bleiben, aber 'localDbData' durch 'dbData' und 'setLocalDbData' durch 'setDbData' ersetzen)
  // Überarbeitete WhatsApp-Funktion, die direkt einen Link öffnet
  const handleWhatsAppClick = (index) => {
    // Speichere die aktuelle Scroll-Position
    saveScrollPosition();
    
    // Prüfe, ob der Index gültig ist
    if (index >= 0 && displayRows && index < displayRows.length) {
      const rowData = displayRows[index];
      
      // Prüfe, ob die Telefonnummer vorhanden ist
      const phoneRaw = rowData.lead_telephone;
      if (!phoneRaw || phoneRaw.trim() === "") {
        alert("Keine Telefonnummer für diesen Kontakt vorhanden.");
        return;
      }
      
      // Extrahiere alle möglichen Telefonnummern
      // Wir verwenden eine Regex, um Telefonnummern zu finden (mit oder ohne Ländervorwahl)
      const phoneMatches = phoneRaw.match(/(?:\+\d{1,4}[\s-]?)?\d+(?:[\s-]?\d+)*/g) || [];
      
      if (phoneMatches.length === 0) {
        alert("Keine gültige Telefonnummer im Format gefunden.");
        return;
      }
      
      // Nehme die erste passende Nummer
      let firstValidPhone = phoneMatches[0];
      
      // Formatiere die Telefonnummer für WhatsApp (entferne alle Nicht-Ziffern außer +)
      const formattedPhone = firstValidPhone.replace(/[^\d+]/g, "");
      
      // WICHTIG: Direktes Update des dbData für sofortige UI-Aktualisierung
      setDbData(prevData => {
        return prevData.map(item => {
          if (item && item.id === rowData.id) {
            return {
              ...item,
              hasWhatsappButtonPressed: true
            };
          }
          return item;
        });
      });
      
      // Zusätzlich auch den editedData aktualisieren
      setEditedData(prevData => ({
        ...prevData,
        [`${rowData.id}-hasWhatsappButtonPressed`]: true
      }));
      
      // Direkte Aktualisierung in der Firebase-Datenbank
      if (userId && rowData.id) {
        const docRef = doc(firestore, `World/${userId}/db/${rowData.id}`);
        updateDoc(docRef, {
          hasWhatsappButtonPressed: true,
          whatsappSentTimestamp: Date.now(),
          whatsappSentBy: userId
        })
        .catch(error => {
          console.error("Fehler beim Aktualisieren des WhatsApp-Status:", error);
        });
      }
      
      const whatsappSettingsRef = ref(database, `/World/${userId}/settings/whatsapp`);
      
      onValue(whatsappSettingsRef, (snapshot) => {
        let whatsappMessage = "";
        let whatsappNumber = formattedPhone;
        
        if (snapshot.exists()) {
          const settings = snapshot.val();
          if (settings.whatsappMessage) {
            whatsappMessage = settings.whatsappMessage.replace(
              /\{\{([a-zA-Z_]+)\}\}/g,
              (match, snippet) => {
                const value = rowData[snippet] || "";
                return value;
              }
            );
          }
          if (settings.whatsappNumber && settings.whatsappNumber.trim() !== "") {
            whatsappNumber = settings.whatsappNumber;
          }
        } else {
          const businessName = rowData.business_name || "";
          if (businessName) {
            whatsappMessage = `Hallo ${businessName}, ich habe Ihre Webseite entdeckt und würde mich gerne über eine mögliche Zusammenarbeit unterhalten.`;
          }
        }
        
        let whatsappUrl = `https://wa.me/${whatsappNumber}`;
        if (whatsappMessage) {
          whatsappUrl += `?text=${encodeURIComponent(whatsappMessage)}`;
        }
        
        window.open(whatsappUrl, "_blank", "noopener,noreferrer");
      }, { onlyOnce: true });
    }
  }

  const handleEmailAiClick = async (index) => {
    saveScrollPosition();
    // Solange das Popup geöffnet ist, sperren wir die Scroll-Position, um Optimistic Updates
    // davon abzuhalten, den gespeicherten Wert zu überschreiben.
    scrollLockedRef.current = true;
    const rowData = displayRows[index];
    if (!rowData) return;

    const hasReplied = rowData.lead_reply === true;
    let selectedTopic = null;

    if (hasReplied && rowData.tags && typeof rowData.tags === 'object') {
        const tagsArray = Object.values(rowData.tags).sort((a, b) => new Date(b.appliedAt || 0) - new Date(a.appliedAt || 0));
        if (tagsArray.length > 0) {
            const newestTag = tagsArray[0];
            selectedTopic = newestTag.selectedTopic;
        }
    }
    
    setEmailPopupData({
      show: true,
      rowData: { ...rowData, selectedTopic },
      index
    });
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) setSelectedRows({});
  };

  const toggleRowSelection = (rowId, isShiftKey = false) => {
    if (isShiftKey && lastSelectedRowId !== null) {
      const lastSelectedIndex = displayRows.findIndex(row => row.id === lastSelectedRowId);
      const currentIndex = displayRows.findIndex(row => row.id === rowId);
      
      if (lastSelectedIndex !== -1 && currentIndex !== -1) {
        const startIdx = Math.min(lastSelectedIndex, currentIndex);
        const endIdx = Math.max(lastSelectedIndex, currentIndex);
        const newSelectedRows = { ...selectedRows };
        for (let i = startIdx; i <= endIdx; i++) {
          if (displayRows[i] && displayRows[i].id) {
            newSelectedRows[displayRows[i].id] = true;
          }
        }
        setSelectedRows(newSelectedRows);
      }
    } else {
      const newValue = !selectedRows[rowId];
      setSelectedRows(prev => ({ ...prev, [rowId]: newValue }));
      if (newValue) setLastSelectedRowId(rowId);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedCount === 0 || !userId) return;
    const countToDelete = selectedCount;
    if (!window.confirm(`Are you sure you want to delete ${countToDelete} contacts?`)) return;

    const idsToDelete = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const deletePromises = idsToDelete.map(id => deleteDoc(doc(firestore, `World/${userId}/db/${id}`)));

    try {
      await Promise.all(deletePromises);
      
      const newDbData = dbData.filter(row => !idsToDelete.includes(row.id));
      setDbData(newDbData);
      itemsToLoadRef.current = newDbData.length;
      
      if (lastDoc && idsToDelete.includes(lastDoc.id)) {
        if (parentOnReload) parentOnReload();
      } else {
        if (parentOnReload) {
          parentOnReload();
        }
        if (filteredCount !== null) {
          setFilteredCount(prev => prev - countToDelete);
        }
      }

      setSelectedRows({});
      setIsSelectMode(false);

    } catch (error) {
      console.error("Error deleting contacts:", error);
    }
  };

  const renderStatus = (rowData, rowId) => {
    if (rowData.tags && Object.keys(rowData.tags).length > 0) {
      const lastTag = Object.values(rowData.tags)[Object.values(rowData.tags).length - 1];
      const statusClass = lastTag.tag.toLowerCase().replace(/\s+/g, '-');
      
      return (
        <span className={`contact-status-badge status-${statusClass}`} style={{ backgroundColor: lastTag.color, color: '#fff' }}>
          {lastTag.tag}
          {lastTag.icon && <img src={lastTag.icon} alt="" className="status-tag-icon" style={{ marginLeft: '4px', width: '14px', height: '14px' }}/>}
        </span>
      );
    }

    const contactStatus = editedData[`${rowId}-lead_contacted`] || rowData.lead_contacted || "Not Contacted";
    const statusClass = contactStatus.toLowerCase().replace(/\s+/g, '-');
    
    let daysSinceLastEmail = "";
    if (rowData.lead_last_email && rowData.lead_last_email.timestamp) {
      const diffDays = Math.floor((Date.now() - parseInt(rowData.lead_last_email.timestamp)) / 86400000);
      daysSinceLastEmail = `| ${diffDays} ${diffDays === 1 ? 'Day' : 'Days'}`;
    }
    
    let shortStatus = contactStatus;
    if (contactStatus.includes("Follow-up") || contactStatus.includes("Follow-Up")) {
        shortStatus = contactStatus.replace(/First|Second|Third/, (m) => ({First: '1.', Second: '2.', Third: '3.'}[m])).replace("Follow-Up", "Follow-up");
    }
    
    return <span className={`contact-status-badge status-${statusClass}`}>{shortStatus} {daysSinceLastEmail && <span className="days-since">{daysSinceLastEmail}</span>}</span>;
  };
  
  const getSelectedContacts = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const selectedContactsComplete = selectedContactIds.map(id => dbData.find(row => row.id === id)).filter(Boolean);
    return selectedContactsComplete.filter(contact => {
        const emails = (contact.lead_email || "").split(/[,;\s]+/).filter(e => e.trim() !== "");
        return emails.some(email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()));
    });
  };

  const handleOpenAiManager = () => {
    const contacts = getSelectedContacts();
    if (contacts.length === 0) {
        alert("None of the selected contacts have a valid email address.");
        return;
    }
    setBulkSelectedRows(contacts.map(c => c.id));
    setBulkContactsData(contacts);
    setIsAiManagerOpen(true);
  };
  
  const handleOpenAutoSend = () => {
    const contacts = getSelectedContacts();
    if (contacts.length === 0) {
        alert("None of the selected contacts have a valid email address.");
        return;
    }
    setBulkSelectedRows(contacts.map(c => c.id));
    setBulkContactsData(contacts);
    setIsAutoSendOpen(true);
  };

  const handleOpenAiCleaner = () => {
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const contacts = selectedContactIds.map(id => dbData.find(row => row.id === id)).filter(Boolean);
    if(contacts.length === 0) return;
    setBulkSelectedRows(selectedContactIds);
    setBulkContactsData(contacts);
    document.body.classList.add('ai-cleaner-open');
    setIsAiCleanerOpen(true);
  };

  const closeBulkPopups = () => {
    setIsAiManagerOpen(false);
    setIsAutoSendOpen(false);
    setBulkSelectedRows([]);
    setBulkContactsData([]);
    setSelectedRows({});
    setIsSelectMode(false);
  };

  const closeAiCleaner = (results = null) => {
    setIsAiCleanerOpen(false);
    document.body.classList.remove('ai-cleaner-open');
    if (results) {
      if (results.removed > 0) {
        onReload();
      } else if (results.renamed && results.renamed.length > 0) {
        setDbData(prevData => {
          const newData = [...prevData];
          results.renamed.forEach(renamedContact => {
            const index = newData.findIndex(item => item.id === renamedContact.id);
            if (index !== -1) {
              newData[index] = {
                ...newData[index],
                business_name: renamedContact.cleaned,
                lead_description: renamedContact.lead_description,
              };
            }
          });
          return newData;
        });
      }
    }
    setCleanerResults(null);
    setBulkSelectedRows([]);
    setBulkContactsData([]);
    setSelectedRows({});
    setIsSelectMode(false);
  };

  const handleSpyClick = async (index) => {
    saveScrollPosition();
    const rowData = displayRows[index];
    if (!rowData || !rowData.lead_website) {
        alert("Keine Website für diesen Eintrag vorhanden.");
        return;
    }
    setSpyLoadingRows(prev => ({ ...prev, [rowData.id]: true }));
    try {
        const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/worldWebCrawler', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ website: rowData.lead_website, rowId: rowData.id, userId })
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const result = await response.json();
        if (result.success && result.formattedData) {
          const { primaryEmail, description } = result.formattedData;
          if (primaryEmail) {
            handleEdit(rowData.id, "lead_email", primaryEmail);
            handleEdit(rowData.id, "is_ready_for_spy", false);
          }
          if (description) handleEdit(rowData.id, "lead_description", description);
        }
    } catch (error) {
        console.error("Fehler beim Web-Crawling:", error);
        alert(`Fehler beim Analysieren der Website: ${error.message}`);
    } finally {
        setSpyLoadingRows(prev => {
          const newState = { ...prev };
          delete newState[rowData.id];
          return newState;
        });
        setTimeout(restoreScrollPosition, 50);
    }
  };
  
  const handleStartMassSpy = () => {
    if (selectedCount === 0) return;
    const selectedContactIds = Object.keys(selectedRows).filter(id => selectedRows[id]);
    const contactsWithWebsite = displayRows
        .filter(row => row && row.id && selectedContactIds.includes(row.id))
        .filter(row => row.lead_website && row.lead_website.trim() !== "");
    
    if (contactsWithWebsite.length === 0) {
        alert("Keiner der ausgewählten Kontakte hat eine Website-URL.");
        return;
    }

    setMassSpyQueue(contactsWithWebsite);
    setMassSpyTotal(contactsWithWebsite.length);
    setMassSpyProgress(0);
    setMassSpyResults({
        processedCount: 0,
        skippedCount: selectedCount - contactsWithWebsite.length,
        emailsFound: 0,
        descriptionsFound: 0,
        successCount: 0,
        errorCount: 0
    });
    setMassSpyContacts(contactsWithWebsite);
    setIsMassSpyActive(true);
  };

  useEffect(() => {
    if (!isMassSpyActive || massSpyQueue.length === 0) return;

    const processNextContact = async () => {
        const currentContact = massSpyQueue[0];
        const remainingQueue = massSpyQueue.slice(1);

        try {
            const response = await fetch('https://us-central1-bonusclub-ab5e2.cloudfunctions.net/worldWebCrawler', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    website: currentContact.lead_website,
                    rowId: currentContact.id,
                    userId
                })
            });
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const result = await response.json();

            if (result.success && result.formattedData) {
                const { primaryEmail, description } = result.formattedData;
                let emailFound = false;
                if (primaryEmail) {
                    handleEdit(currentContact.id, "lead_email", primaryEmail);
                    handleEdit(currentContact.id, "is_ready_for_spy", false);
                    emailFound = true;
                    currentContact._emailFound = true;
                }
                if (description) handleEdit(currentContact.id, "lead_description", description);
                setMassSpyResults(prev => ({ ...prev, successCount: prev.successCount + 1, emailsFound: prev.emailsFound + (emailFound ? 1 : 0) }));
            } else {
                throw new Error("Keine gültigen Daten erhalten");
            }
        } catch (error) {
            console.error(`Fehler beim Mass Spy für ${currentContact.id}:`, error);
            setMassSpyResults(prev => ({ ...prev, errorCount: prev.errorCount + 1 }));
        } finally {
            setMassSpyProgress(prev => prev + 1);
            setMassSpyQueue(remainingQueue);
        }
    };

    processNextContact();
  }, [isMassSpyActive, massSpyQueue, userId]);

  const closeMassSpy = () => {
    setIsMassSpyActive(false);
    setMassSpyQueue([]);
    setSelectedRows({});
    setIsSelectMode(false);
  };

  const handleDeleteNoEmailEntries = async () => {
    if (!userId) return;
    const contactsToDelete = massSpyContacts.filter(c => !c._emailFound).map(c => c.id);
    if (contactsToDelete.length === 0) {
      alert("No contacts without a found email to delete.");
      return;
    };
    if (!window.confirm(`Are you sure you want to delete ${contactsToDelete.length} entries where no email was found?`)) return;
    
    const deletePromises = contactsToDelete.map(id => deleteDoc(doc(firestore, `World/${userId}/db/${id}`)));
    
    try {
        await Promise.all(deletePromises);
        
        const newDbData = dbData.filter(row => !contactsToDelete.includes(row.id));
        setDbData(newDbData);
        itemsToLoadRef.current = newDbData.length;
        
        if (lastDoc && contactsToDelete.includes(lastDoc.id)) {
            if (parentOnReload) parentOnReload();
        } else {
            if (parentOnReload) {
                parentOnReload();
            }
            if (filteredCount !== null) {
                setFilteredCount(prev => prev - contactsToDelete.length);
            }
        }
    } catch (error) {
        console.error("Error deleting entries without email:", error);
        alert("An error occurred while deleting entries.");
    }
  };

  const openAiManagerWithCleanedContacts = (contactIds, contactsData) => {
    setBulkSelectedRows(contactIds);
    setBulkContactsData(contactsData);
    setIsAiManagerOpen(true);
    setCleanerResults(null);
  };

  const openAutoSendWithCleanedContacts = (contactIds, contactsData) => {
    setBulkSelectedRows(contactIds);
    setBulkContactsData(contactsData);
    setIsAutoSendOpen(true);
    setCleanerResults(null);
  };

  const handleStatusFilterChange = (newStatus) => {
    setStatusFilter(newStatus);
  };

  const handleImportFilterChange = (newImport) => {
    setImportFilter(newImport);
  };

  const handleSearch = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  const handleAdvancedFilterChange = (e) => {
    const { name, value } = e.target;
    setAdvancedFilters(prev => ({ ...prev, [name]: value }));
  };

  const resetAdvancedFilters = () => {
    setAdvancedFilters({
      city: '',
      has_email: 'any',
      has_website: 'any',
      has_instagram: 'any',
      range_field: 'lead_rating',
      range_min: '',
      range_max: '',
    });
  };

  const sortableColumns = useMemo(() => [
    "lead_rating", 
    "lead_reviews", 
    "lead_stars", 
    "business_name", 
    "lead_firstname",
    "lead_lastname",
    "lead_email",
    "lead_website",
    "lead_instagram",
    "lead_city",
    "timestamp", 
    "import_name"
  ], []);

  return (
    <div className="worldDbTableContainer" ref={tableContainerRef}>
      <WorldDbTableNav
        setShowUploader={setShowUploader}
        displayedCount={displayedCount}
        filteredCount={filteredCount}
        contactedToday={contactedToday}
        isSelectMode={isSelectMode}
        toggleSelectMode={toggleSelectMode}
        selectedCount={selectedCount}
        handleDeleteSelected={handleDeleteSelected}
        totalDatabaseCount={totalDatabaseCount}
        handleOpenAiManager={handleOpenAiManager}
        handleOpenAutoSend={handleOpenAutoSend}
        handleStartMassSpy={handleStartMassSpy}
        handleOpenAiCleaner={handleOpenAiCleaner}
        statusFilterMode={statusFilter}
        onStatusFilterChange={handleStatusFilterChange}
        importFilterMode={importFilter}
        onImportFilterChange={handleImportFilterChange}
        importOptions={importOptions}
        onSearch={handleSearch}
        onToggleAdvancedFilters={() => setShowAdvancedFilters(!showAdvancedFilters)}
        isAdvancedFilterVisible={showAdvancedFilters}
      />
      {showAdvancedFilters && (
        <div className="advanced-filters-panel">
          <div className="filter-group">
            <label>City</label>
            <input type="text" name="city" value={advancedFilters.city} onChange={handleAdvancedFilterChange} placeholder="e.g. Berlin" />
          </div>
          <div className="filter-group">
            <label>Has Email</label>
            <select name="has_email" value={advancedFilters.has_email} onChange={handleAdvancedFilterChange}>
              <option value="any">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Has Website</label>
            <select name="has_website" value={advancedFilters.has_website} onChange={handleAdvancedFilterChange}>
              <option value="any">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Has Instagram</label>
            <select name="has_instagram" value={advancedFilters.has_instagram} onChange={handleAdvancedFilterChange}>
              <option value="any">Any</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
          <div className="filter-group range-filter">
            <label>Range Filter</label>
            <select name="range_field" value={advancedFilters.range_field} onChange={handleAdvancedFilterChange}>
              <option value="lead_rating">Rating</option>
              <option value="lead_reviews">Reviews</option>
              <option value="lead_stars">Stars</option>
            </select>
            <input type="number" name="range_min" value={advancedFilters.range_min} onChange={handleAdvancedFilterChange} placeholder="Min" />
            <input type="number" name="range_max" value={advancedFilters.range_max} onChange={handleAdvancedFilterChange} placeholder="Max" />
          </div>
           <div className="filter-group actions">
            <button onClick={resetAdvancedFilters}>Reset Filters</button>
          </div>
        </div>
      )}
      <div className="table-content-wrapper">
        {loading && !loadingMore && dbData.length > 0 && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}
        <table className="worldDbTable" onWheel={handleWheel} ref={tableRef}>
          <thead>
            <tr>
              {orderedColumns.map((colKey) => {
                if (colKey === "selectCheckbox") {
                  return isSelectMode ? <th key={colKey} className="worldDbTableHeader selectCheckbox"><div></div></th> : null;
                }
                const headerLabel = availableFields[colKey] || {
                    rowNumber: "Nr.", status: "Status", lead_type: "Type", lead_description: "Description",
                    timestamp: "Days", lastEdited: "Last Edited", contacted: "Lead Status",
                    aiMail: "AI Mail", spy: "Spy", whatsapp: "WhatsApp"
                }[colKey] || colKey;
                const isSortable = sortableColumns.includes(colKey);
                return (
                  <th key={colKey} className={`worldDbTableHeader ${colKey} ${isSortable ? 'sortable' : ''}`} data-column={colKey}>
                    <div className="column-header-content">
                      {headerLabel}
                      {renderSortButtons(colKey)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={loading && !loadingMore ? 'reloading-data' : ''}>
            {loading && dbData.length === 0 ? (
              <tr>
                  <td colSpan={orderedColumns.length + (isSelectMode ? 1 : 0)} className="initial-loading-cell">
                      <div className="initial-loading-spinner">
                          <div className="spinner"></div>
                          <span>Loading Contacts...</span>
                      </div>
                  </td>
              </tr>
            ) : noFilterResults ? (
              <tr className="no-results-row">
                <td colSpan={orderedColumns.length + (isSelectMode ? 1 : 0)} className="no-results-cell">
                  <div className="no-results-content">
                    <div className="no-results-icon">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M10 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8zm0-14a6 6 0 1 0 0 12 6 6 0 0 0 0-12z" fill="currentColor" />
                        <path d="M21.707 20.293l-4-4a1 1 0 0 0-1.414 1.414l4 4a1 1 0 0 0 1.414-1.414z" fill="currentColor" />
                        <path d="M14 9H6a1 1 0 0 0 0 2h8a1 1 0 0 0 0-2z" fill="currentColor" />
                      </svg>
                    </div>
                    <h3 className="no-results-title">No matching contacts found</h3>
                    <p className="no-results-description">
                      No contacts were found that match the current filter criteria.
                    </p>
                    <button 
                      className="no-results-reset-btn"
                      onClick={() => {
                        handleSearch('');
                        handleStatusFilterChange('all');
                        handleImportFilterChange('all');
                        handleSortChange('timestamp', 'desc');
                      }}
                    >
                       <svg width="14" height="14" viewBox="0 0 24 24" fill="none" >
                         <path d="M17.65 6.35A7.958 7.958 0 0 0 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0 1 12 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor" />
                       </svg>
                      Reset Filters
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              displayRows.map((row, index) => {
                const rowId = row.id;
                const hasReplied = row.lead_reply === true;
                return (
                  <tr key={rowId || index} className={`worldDbTableRow ${hasReplied ? 'has-replied' : ''}`}>
                    {orderedColumns.map((colKey) => {
                      if (colKey === "selectCheckbox") {
                        return isSelectMode ? (
                          <td key={colKey} className="worldDbTableCell selectCheckbox">
                            <input type="checkbox" checked={!!selectedRows[rowId]} onChange={(e) => toggleRowSelection(rowId, e.nativeEvent.shiftKey)}/>
                          </td>
                        ) : null;
                      }
                      if (colKey === "rowNumber") {
                        return <td key={colKey} className={`worldDbTableCell rowNumber`}>{index + 1}</td>;
                      }
                      if (colKey === "contacted") {
                        return <td key={colKey} className={`worldDbTableCell contactedCell`}>{renderStatus(row, rowId)}</td>;
                      }
                      if (colKey === "aiMail") {
                        const hasEmail = (row.lead_email || "").split(/[,;\s]+/).some(e => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()));
                        const hasReplied = row.lead_reply === true;
                        return (
                          <td key={colKey} className={`worldDbTableCell aiMail`}>
                            <button className={`ai-mail-btn ${!hasEmail ? 'disabled' : ''} ${hasReplied ? 'replied' : ''}`} onClick={() => hasEmail && handleEmailAiClick(index)} disabled={!hasEmail} title={!hasEmail ? "No valid email" : hasReplied ? "Lead replied" : "Create AI Mail"}>
                              {hasReplied ? (<><span className="replied-icon">✓</span><p style={{fontSize: '9px'}}>REPLIED</p></>) : (<><div className="ai-chat-icon-container"><img src="/img/mailerle.webp" alt="Chat" className="ai-chat-icon" /></div> AI MAIL</>)}
                            </button>
                          </td>
                        );
                      }
                      if (colKey === "spy") {
                        const hasWebsite = row.lead_website && row.lead_website.trim() !== "";
                        const isLoading = spyLoadingRows[row.id] === true;
                        return (
                          <td key={colKey} className={`worldDbTableCell spy`}>
                            <button className={`spy-btn ${!hasWebsite ? 'disabled' : ''} ${isLoading ? 'loading' : ''}`} onClick={() => hasWebsite && !isLoading && handleSpyClick(index)} disabled={!hasWebsite || isLoading} title={!hasWebsite ? "Website-URL fehlt" : isLoading ? "Analysiere..." : "Website analysieren"}>
                              {isLoading ? (<><div className="spy-spinner"></div>RUN</>) : (<><div className="ai-spy-icon-container"><img src="/img/binocul.svg" alt="Spy" className="spy-icon" /></div>SPY</>)}
                            </button>
                          </td>
                        );
                      }
                      if (colKey === "whatsapp") {
                        const hasWhatsappSent = row.hasWhatsappButtonPressed || false;
                        return (
                          <td key={colKey} className={`worldDbTableCell whatsapp`}>
                            <button className={`whatsapp-btn ${hasWhatsappSent ? 'whatsapp-sent' : ''}`} onClick={() => handleWhatsAppClick(index)}>
                              <div className="whatsapp-icon-container"><img src="/img/whats.svg" alt="WhatsApp" className="whatsapp-icon" /></div>{hasWhatsappSent ? 'SENT' : 'WHATS'}
                            </button>
                          </td>
                        );
                      }
                      if (colKey === "lead_type") {
                        const currentValue = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                        return (
                          <td key={colKey} className={`worldDbTableCell ${colKey}`}>
                            <select value={currentValue} onChange={(e) => handleEdit(rowId, colKey, e.target.value)}>
                              <option value="">Select type</option>
                              {typeOptions.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                          </td>
                        );
                      }
                      if (colKey === "lead_reply") {
                        const hasReplied = row ? (editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? false) : false;
                        return <td key={colKey} className={`worldDbTableCell ${colKey}`}>{hasReplied ? <span className="replied-badge"><span className="replied-icon">✓</span> Replied</span> : <span className="not-replied-badge">Pending</span>}</td>
                      }
                      if (colKey === "timestamp") {
                        const timestampValue = row.timestamp;
                        let daysAgo = "";
                        if (timestampValue) {
                          const diffDays = Math.floor((new Date() - new Date(timestampValue)) / 86400000);
                          daysAgo = diffDays + " day" + (diffDays === 1 ? "" : "s");
                        }
                        return <td key={colKey} className={`worldDbTableCell ${colKey}`}>{daysAgo}</td>;
                      }
                      if (colKey === "lastEdited") {
                          const lastEditValue = (row.lead_last_edit && row.lead_last_edit.time) || "";
                          let hoursAgo = "";
                          if (lastEditValue) {
                              const diffHours = Math.floor((new Date() - new Date(lastEditValue)) / 3600000);
                              hoursAgo = diffHours + " h";
                          }
                          return <td key={colKey} className={`worldDbTableCell ${colKey}`}>{hoursAgo}</td>;
                      }
                      if (colKey === "lead_website") {
                          const websiteUrl = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                          const openWebsite = (e) => {
                              e.stopPropagation();
                              let url = websiteUrl.trim();
                              if (url && !url.startsWith('http')) url = 'https://' + url;
                              if (url) window.open(url, '_blank', 'noopener,noreferrer');
                          };
                          return (
                              <td key={colKey} className={`worldDbTableCell ${colKey} website-cell`}>
                                  <div className="website-container">
                                      <div className="website-content" contentEditable suppressContentEditableWarning onBlur={(e) => handleEdit(rowId, colKey, e.target.textContent)}>{websiteUrl || "-"}</div>
                                      {websiteUrl && (<button className="external-link-btn" onClick={openWebsite} title="Website in neuem Tab öffnen"><svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg></button>)}
                                  </div>
                              </td>
                          );
                      }
                      // Default editable cell
                      const cellValue = editedData[`${rowId}-${colKey}`] ?? row[colKey] ?? "";
                      return (
                          <td key={colKey} className={`worldDbTableCell ${colKey}`} contentEditable suppressContentEditableWarning onBlur={(e) => handleEdit(rowId, colKey, e.target.textContent)}>
                              {cellValue || (colKey === "lead_description" ? "-" : "")}
                          </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
            {hasMoreData && !noFilterResults && (
              <tr className="load-more-row">
                <td colSpan={orderedColumns.length + (isSelectMode ? 1 : 0)} className="load-more-cell">
                  <div className="table-load-more-container">
                    <button className="table-load-more-button" onClick={loadMoreData} disabled={loadingMore}>
                      {loadingMore ? 'Loading...' : 'Load More Contacts'}
                    </button>
                    <span className="table-load-more-info">
                      {dbData.length} of {filteredCount ?? totalDatabaseCount} contacts displayed
                    </span>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      <EmailPopup 
        isPopupOpen={emailPopupData.show}
        displayRows={displayRows}
        selectedRowIndex={emailPopupData.index}
        userId={userId}
        onEmailResult={handleEmailResult}
        closePopup={() => setEmailPopupData({ show: false, rowData: null, index: null })}
        rowData={emailPopupData.rowData}
      />

      <BulkEmailPopup 
        isAiManagerOpen={isAiManagerOpen}
        isAutoSendOpen={isAutoSendOpen}
        bulkSelectedRows={bulkSelectedRows}
        bulkContactsData={bulkContactsData}
        localDbData={dbData}
        userId={userId}
        onEmailResult={handleEmailResult}
        closeAiManager={closeBulkPopups}
        closeAutoSend={closeBulkPopups}
      />

      <MassSpyPopup 
        isMassSpyActive={isMassSpyActive}
        massSpyProgress={massSpyProgress}
        massSpyTotal={massSpyTotal}
        massSpyQueue={massSpyQueue}
        massSpyResults={massSpyResults}
        closeMassSpy={closeMassSpy}
        onDeleteNoEmailEntries={handleDeleteNoEmailEntries}
      />
      
      {isAiCleanerOpen && (
        <AiCleanerPopup 
          isOpen={isAiCleanerOpen}
          bulkSelectedRows={bulkSelectedRows}
          bulkContactsData={bulkContactsData}
          userId={userId}
          closeAiCleaner={closeAiCleaner}
          onOpenAiManager={openAiManagerWithCleanedContacts}
          onOpenAutoSend={openAutoSendWithCleanedContacts}
        />
      )}
    </div>
  );
};

export default NewWorldDbTable; 